import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Tooltip,
  Paper,
  Skeleton
} from '@mui/material';
import {
  Add as AddIcon,
  Category as CategoryIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import api from '../../../../services/api';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';

const BrandCategorySelector = ({
  brand,           // Full brand object (for standalone use)
  brandId,         // Brand ID (for compact/table use)
  currentCategories = [],  // Current categories (for compact/table use)
  onUpdate,
  compact = false  // Compact mode for tables
}) => {
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [optimisticCategories, setOptimisticCategories] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Categorias carregadas da API (fonte unica da verdade: backend/config/categories.js)
  const [availableCategories, setAvailableCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Buscar categorias da API ao abrir o dialog
  const fetchCategories = async () => {
    if (availableCategories.length > 0) return; // Ja carregado

    try {
      setLoadingCategories(true);
      const res = await api.get('/brands/categories');
      if (res.data.success) {
        setAvailableCategories(res.data.data);
      }
    } catch (err) {
      console.error('Erro ao carregar categorias:', err);
      setError('Erro ao carregar lista de categorias');
    } finally {
      setLoadingCategories(false);
    }
  };

  // Support both brand object and individual props
  const effectiveBrandId = brand?._id || brandId;
  const externalCategories = brand?.relevantCategories || currentCategories;
  const effectiveCategories = optimisticCategories !== null ? optimisticCategories : externalCategories;

  // Stringify for dependency comparison
  const categoriesKey = externalCategories.join(',');
  useEffect(() => {
    setOptimisticCategories(null);
    setSelectedCategories([...externalCategories]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesKey]);

  const handleOpenDialog = () => {
    setSelectedCategories([...effectiveCategories]);
    setDialogOpen(true);
    setError('');
    setSuccess('');
    fetchCategories(); // Carregar categorias ao abrir
  };

  const handleToggleCategory = (category) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');

      const res = await api.put(`/brands/${effectiveBrandId}/categories`, {
        categories: selectedCategories
      });

      setSuccess('Categorias atualizadas com sucesso!');

      const recalculation = res.data.recalculation || res.data.recalculated;
      if (recalculation?.queued) {
        setSuccess(prev =>
          `${prev} Recalculo de tiers iniciado em segundo plano.`
        );
      } else if (recalculation?.updated !== undefined) {
        setSuccess(prev =>
          `${prev} ${recalculation.updated} artigos recalculados.`
        );
      }

      if (onUpdate) {
        onUpdate(res.data.data);
      }
      setOptimisticCategories(res.data.data?.relevantCategories || selectedCategories);

      setTimeout(() => {
        setDialogOpen(false);
        setSuccess('');
      }, 1500);

    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar categorias');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCategory = async (category) => {
    const newCategories = effectiveCategories.filter(c => c !== category);

    try {
      setLoading(true);
      const res = await api.put(`/brands/${effectiveBrandId}/categories`, {
        categories: newCategories
      });

      if (onUpdate) {
        onUpdate(res.data.data);
      }
      setOptimisticCategories(res.data.data?.relevantCategories || newCategories);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao remover categoria');
    } finally {
      setLoading(false);
    }
  };

  // Compact mode for tables - inline chips with edit button
  if (compact) {
    return (
      <>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
          {effectiveCategories.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Nenhuma
            </Typography>
          ) : (
            effectiveCategories.slice(0, 3).map((category, idx) => {
              // category pode ser string ou objeto {value, label} (dados legados)
              const catValue = typeof category === 'string' ? category : category?.value;
              const catLabel = typeof category === 'string' ? category : (category?.label || category?.value);
              if (!catValue) return null;
              return (
                <Chip
                  key={catValue || idx}
                  label={catLabel}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 22 }}
                />
              );
            })
          )}
          {effectiveCategories.length > 3 && (
            <Chip
              label={`+${effectiveCategories.length - 3}`}
              size="small"
              variant="filled"
              sx={{ fontSize: '0.7rem', height: 22 }}
            />
          )}
          <Tooltip title="Editar categorias relevantes">
            <Button
              size="small"
              onClick={handleOpenDialog}
              sx={{ minWidth: 'auto', p: 0.5 }}
            >
              <AddIcon fontSize="small" />
            </Button>
          </Tooltip>
        </Box>

        {/* Dialog (shared with full mode) */}
        <PlatformDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <PlatformDialogTitle
            title="Categorias Relevantes"
            subtitle="Configure as categorias de maior impacto para esta marca."
            icon={<CategoryIcon />}
            onClose={() => setDialogOpen(false)}
          />
          <DialogContent sx={{ pt: 3, pb: 2 }}>
            <Alert severity="info" variant="outlined" sx={{ mb: 2.5, borderRadius: 2 }}>
              Fontes nestas categorias receberão <strong>Tier 1</strong> automaticamente (Nicho de Autoridade).
            </Alert>

            {error && (
              <Alert severity="error" variant="outlined" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                {success}
              </Alert>
            )}

            {loadingCategories ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[1, 2, 3, 4].map(i => (
                  <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 2 }} />
                ))}
              </Box>
            ) : (
              <FormGroup sx={{ gap: 0.5 }}>
                {availableCategories.map(cat => (
                  <FormControlLabel
                    key={cat.value}
                    control={
                      <Checkbox
                        checked={selectedCategories.includes(cat.value)}
                        onChange={() => handleToggleCategory(cat.value)}
                        disabled={loading}
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>{cat.value}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                          {cat.description}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      ml: 0,
                      mr: 0,
                      px: 1,
                      borderRadius: 2,
                      '&:hover': { bgcolor: 'action.hover' },
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 0 }
                    }}
                  />
                ))}
              </FormGroup>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.5 }}>
            <Button onClick={() => setDialogOpen(false)} disabled={loading} sx={{ fontWeight: 600 }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading || loadingCategories}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              sx={{ fontWeight: 700, borderRadius: 2, px: 3, boxShadow: 'none' }}
            >
              Salvar ({selectedCategories.length})
            </Button>
          </DialogActions>
        </PlatformDialog>
      </>
    );
  }

  // Full mode (standalone)
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CategoryIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" color="text.secondary">
          Categorias Relevantes
        </Typography>
        <Tooltip title="Fontes nestas categorias recebem Tier 1 automaticamente">
          <InfoIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
        </Tooltip>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          minHeight: 48,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          alignItems: 'center'
        }}
      >
        {effectiveCategories.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhuma categoria selecionada (usa apenas Tier por Tamanho)
          </Typography>
        ) : (
          effectiveCategories.map((category, idx) => {
            // category pode ser string ou objeto {value, label} (dados legados)
            const catValue = typeof category === 'string' ? category : category?.value;
            const catLabel = typeof category === 'string' ? category : (category?.label || category?.value);
            if (!catValue) return null;
            return (
              <Chip
                key={catValue || idx}
                label={catLabel}
                size="small"
                onDelete={() => handleRemoveCategory(catValue)}
                color="primary"
                variant="outlined"
              />
            );
          })
        )}

        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{ ml: 'auto' }}
        >
          {effectiveCategories.length > 0 ? 'Editar' : 'Adicionar'}
        </Button>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Dialog para selecionar categorias */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Selecionar Categorias Relevantes
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Fontes nestas categorias receberao <strong>Tier 1</strong> automaticamente.
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {loadingCategories ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} variant="rectangular" height={48} />
              ))}
            </Box>
          ) : (
            <FormGroup>
              {availableCategories.map(cat => (
                <FormControlLabel
                  key={cat.value}
                  control={
                    <Checkbox
                      checked={selectedCategories.includes(cat.value)}
                      onChange={() => handleToggleCategory(cat.value)}
                      disabled={loading}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">{cat.value}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {cat.description}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    mb: 0.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 0 }
                  }}
                />
              ))}
            </FormGroup>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || loadingCategories}
            startIcon={loading && <CircularProgress size={16} />}
          >
            Salvar ({selectedCategories.length} selecionadas)
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BrandCategorySelector;
